import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { DICT } from "@/lib/i18n";
import { api, intf, getToken, setToken, setIncludeSaisies, setApiLang } from "@/lib/api";
import { enregistrerDashboard, lireDashboard, enregistrerContexte, lireContexte, oublierContexte, estErreurReseau } from "@/lib/horsligne";
import PasswordGate from "@/components/PasswordGate";
import QuestionsGate from "@/components/QuestionsGate";
import HomeView, { Logo } from "@/components/HomeView";
import LoginView from "@/components/LoginView";
import { Globe, Database, BarChart3, GitCompare, Boxes, History, Trash2, Map as MapIcon, ClipboardCheck, Users, LogOut, Loader2, PenLine, BellRing, FileText, Search, PanelLeftClose, PanelLeftOpen, Menu, X, WifiOff, RefreshCw, Download } from "lucide-react";

const Dashboard = lazy(() => import("@/components/Dashboard"));
const QcView = lazy(() => import("@/components/QcView"));
const MapView = lazy(() => import("@/components/MapView"));
const OverlapView = lazy(() => import("@/components/OverlapView"));
const SearchView = lazy(() => import("@/components/SearchView"));
const SaisieView = lazy(() => import("@/components/SaisieView"));
const AlertsView = lazy(() => import("@/components/AlertsView"));
const ReportsView = lazy(() => import("@/components/ReportsView"));
const ImportView = lazy(() => import("@/components/ImportView"));
const SchemaView = lazy(() => import("@/components/SchemaView"));
const JournalView = lazy(() => import("@/components/JournalView"));
const UsersView = lazy(() => import("@/components/UsersView"));
const AccountPanel = lazy(() => import("@/components/AccountPanel"));
const Copilote = lazy(() => import("@/components/Copilote"));
const MethodsView = lazy(() => import("@/components/MethodsView"));
const LogicielView = lazy(() => import("@/components/LogicielView"));
const InstallationView = lazy(() => import("@/components/InstallationView"));
const ResetPasswordView = lazy(() => import("@/components/ResetPasswordView"));

const Chargement = () => (
  <div style={{ display: "grid", placeItems: "center", padding: "80px 0" }} data-testid="view-loading">
    <Loader2 className="animate-spin" color="var(--accent)" />
  </div>
);

const SHEET_VIEWS = ["analyse", "qc", "map"];
const OFFLINE_VIEWS = ["analyse", "saisie", "map", "search", "qc"];
const ALL_VIEWS = ["analyse", "qc", "map", "overlap", "search", "saisie", "alerts", "reports", "import", "schema", "journal", "users"];
const parseHash = () => {
  const h = decodeURIComponent(window.location.hash.replace(/^#\/?/, ""));
  if (h.startsWith("echantillon/")) return { view: "search", query: h.slice(12) };
  if (ALL_VIEWS.includes(h)) return { view: h, query: "" };
  return null;
};

const parseReset = () => {
  const h = decodeURIComponent(window.location.hash.replace(/^#\/?/, ""));
  return h.startsWith("reinitialiser/") ? h.slice(14) : "";
};

export default function App() {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "fr");
  const [user, setUser] = useState(null); // null = vérification, false = non connecté
  const [pub, setPub] = useState(() => (window.location.hash === "#login" || parseHash() ? "login" : "home"));
  const [view, setView] = useState(() => parseHash()?.view || new URLSearchParams(window.location.search).get("view") || "analyse");
  const [current, setCurrent] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [analyse, setAnalyse] = useState(null);
  const [tab, setTab] = useState("delays");
  const [withSaisies, setWithSaisies] = useState(() => localStorage.getItem("withSaisies") === "1");
  const toggleSaisies = () => { const v = !withSaisies; setWithSaisies(v); setIncludeSaisies(v); localStorage.setItem("withSaisies", v ? "1" : "0"); };
  setIncludeSaisies(withSaisies);
  const [overlap, setOverlap] = useState(null);
  const [schema, setSchema] = useState(null);
  const [journal, setJournal] = useState([]);
  const [alert, setAlert] = useState(null);
  const [query, setQuery] = useState(() => parseHash()?.query || "");
  const [hdrQ, setHdrQ] = useState("");
  const [imports, setImports] = useState([]);
  const [account, setAccount] = useState(false);
  const [resetTok, setResetTok] = useState(() => parseReset());
  const [installation, setInstallation] = useState(null);
  useEffect(() => { api.installationEtat().then((d) => setInstallation(!!d.necessite_config)).catch(() => setInstallation(false)); }, []);
  const [railOpen, setRailOpen] = useState(() => localStorage.getItem("rail") !== "0");
  const [railMobile, setRailMobile] = useState(false);
  const analyseCache = useRef(new Map());
  const [horsLigne, setHorsLigne] = useState(!navigator.onLine);
  const [dateCache, setDateCache] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const toggleRail = () => { const v = !railOpen; setRailOpen(v); localStorage.setItem("rail", v ? "1" : "0"); };
  const t = DICT[lang];
  setApiLang(lang);
  const goSearch = (q) => { if (!q.trim()) return; setQuery(q.trim()); setView("search"); setHdrQ(""); window.scrollTo(0, 0); };

  useEffect(() => {
    const onHash = () => { setResetTok(parseReset()); if (window.location.hash === "#login") setPub("login"); const p = parseHash(); if (p) { setView(p.view); if (p.view === "search") setQuery(p.query); } };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  useEffect(() => {
    if (!user) return;
    const want = view === "search" && query ? `#echantillon/${encodeURIComponent(query)}` : `#${view}`;
    if (window.location.hash !== want) window.history.replaceState(null, "", want);
  }, [view, query, user]);

  useEffect(() => { if (user && !horsLigne) api.alertes().then((a) => setAlert(a.latest && a.latest.date === a.today ? a.latest : null)).catch(() => {}); }, [user, view, horsLigne]);

  useEffect(() => { localStorage.setItem("lang", lang); document.documentElement.lang = lang; document.title = DICT[lang].appName; }, [lang]);


  useEffect(() => {
    const maj = () => setHorsLigne(!navigator.onLine);
    const onInstall = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("online", maj);
    window.addEventListener("offline", maj);
    window.addEventListener("beforeinstallprompt", onInstall);
    return () => { window.removeEventListener("online", maj); window.removeEventListener("offline", maj); window.removeEventListener("beforeinstallprompt", onInstall); };
  }, []);

  useEffect(() => {
    if (!getToken()) { setUser(false); return; }
    api.me().then(setUser).catch((e) => {
      const hl = estErreurReseau(e) ? lireContexte() : null;
      if (hl) { setUser(hl.user); setHorsLigne(true); setDateCache(hl.le); return; }
      setToken(null); setUser(false);
    });
    const onExp = () => setUser(false);
    window.addEventListener("auth:expired", onExp);
    return () => window.removeEventListener("auth:expired", onExp);
  }, []);

  const refreshMeta = useCallback(async () => {
    try {
      const [sc, jr] = await Promise.all([api.schema(), api.journal()]);
      setSchema(sc); setJournal(jr);
    } catch (e) { if (!estErreurReseau(e)) throw e; }
  }, []);

  const loadImport = useCallback(async (imp) => {
    setCurrent(imp);
    const dataSheets = imp.sheets.filter((s) => !s.pivot && s.n > 0);
    const def = imp.latest && dataSheets.find((s) => s.name === imp.latest) ? imp.latest : (dataSheets[0]?.name || null);
    setSheet(def);
    try { setOverlap(await api.overlap(imp.import_id || imp.id)); } catch (e) { if (!estErreurReseau(e)) throw e; }
    refreshMeta();
  }, [refreshMeta]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const imps = await api.imports();
        setImports(imps);
        if (imps.length) await loadImport(imps[0]); else { refreshMeta(); setView((v) => (SHEET_VIEWS.includes(v) ? "import" : v)); }
      } catch (e) {
        const hl = estErreurReseau(e) ? lireContexte() : null;
        if (!hl) return;
        setHorsLigne(true); setDateCache(hl.le); setImports(hl.imports || []); setSchema(hl.schema || null);
        if (hl.imports?.length) { setCurrent(hl.imports[0]); setSheet(hl.imports[0].latest || hl.imports[0].sheets?.find((s) => !s.pivot && s.n > 0)?.name || null); }
        setView("analyse");
      }
    })();
  }, [user, loadImport, refreshMeta]);

  const reloadImports = useCallback(async () => {
    const imps = await api.imports();
    setImports(imps);
    if (imps.length) await loadImport(imps[0]);
    else { setCurrent(null); setSheet(null); setAnalyse(null); setOverlap(null); refreshMeta(); }
  }, [loadImport, refreshMeta]);

  useEffect(() => {
    if (!current || !sheet) { setAnalyse(null); return; }
    const id = current.import_id || current.id;
    const cle = `${id}|${sheet}|${withSaisies}|${lang}`;
    const enCache = analyseCache.current.get(cle);
    if (enCache) { setAnalyse(enCache); setTab("delays"); return; }
    setAnalyse(null);
    api.analyse(id, sheet).then((a) => {
      if (analyseCache.current.size >= 12) analyseCache.current.delete(analyseCache.current.keys().next().value);
      analyseCache.current.set(cle, a); setAnalyse(a); setDateCache(null);
      enregistrerDashboard(cle, a);
    }).catch((e) => {
      const local = estErreurReseau(e) ? lireDashboard(cle) : null;
      if (local) { setAnalyse(local.analyse); setHorsLigne(true); setDateCache(local.le); }
    });
    setTab("delays");
  }, [current, sheet, withSaisies, lang]);

  // Préchargement des écrans utilisables hors ligne : leur code doit être en cache avant de partir sur le terrain.
  useEffect(() => {
    if (!user || horsLigne) return;
    const h = setTimeout(() => {
      import("@/components/SaisieView").catch(() => {});
      import("@/components/Dashboard").catch(() => {});
      import("@/components/MapView").catch(() => {});
      import("@/components/SearchView").catch(() => {});
      import("@/components/QcView").catch(() => {});
      api.saisieRefs().then((r) => localStorage.setItem("saisie_refs", JSON.stringify(r))).catch(() => {});
    }, 2500);
    return () => clearTimeout(h);
  }, [user, horsLigne]);

  useEffect(() => { if (user && !horsLigne && imports.length) enregistrerContexte(user, imports, schema); }, [user, horsLigne, imports, schema]);

  const reconnecter = useCallback(async () => {
    analyseCache.current.clear(); setDateCache(null);
    try {
      const u = await api.me();
      setUser(u); setHorsLigne(false);
      const imps = await api.imports();
      setImports(imps);
      if (imps.length) await loadImport(imps[0]);
    } catch { setHorsLigne(true); }
  }, [loadImport]);

  useEffect(() => { if (user && !horsLigne && view === "journal") refreshMeta(); }, [view, user, horsLigne, refreshMeta]);

  const onCommitted = async (r) => { setImports(await api.imports()); await loadImport(r); setView("analyse"); };
  const doReset = async () => {
    if (!window.confirm(t.reset + " ?")) return;
    await api.reset(); setCurrent(null); setSheet(null); setAnalyse(null); setOverlap(null); refreshMeta(); setView("import");
  };
  const logout = async () => { try { await api.logout(); } catch (e) { /* jeton déjà expiré */ } setToken(null); oublierContexte(); setUser(false); setCurrent(null); setPub("home"); };

  if (user === null) return <div style={{ minHeight: "100vh", background: "var(--bg)", display: "grid", placeItems: "center" }}><Loader2 className="animate-spin" color="var(--accent)" /></div>;
  if (user === false) {
    if (installation) return <Suspense fallback={<Chargement />}><InstallationView t={t} lang={lang} setLang={setLang} onDone={(u) => { setInstallation(false); setUser(u); setView("analyse"); }} /></Suspense>;
    const goLogin = () => { setPub("login"); window.scrollTo(0, 0); };
    const goNav = (p) => { setPub(p); window.scrollTo(0, 0); };
    if (resetTok) return <Suspense fallback={<Chargement />}><ResetPasswordView t={t} token={resetTok} onDone={() => { setResetTok(""); window.history.replaceState(null, "", "#login"); setPub("login"); }} /></Suspense>;
    if (["home", "analyses", "stakes", "solution"].includes(pub)) return <HomeView t={t} lang={lang} setLang={setLang} page={pub} onNav={goNav} onLogin={goLogin} onMethods={() => goNav("methods")} />;
    if (pub === "methods") return <Suspense fallback={<Chargement />}><MethodsView t={t} lang={lang} setLang={setLang} onLogin={goLogin} onBack={() => setPub("home")} /></Suspense>;
    if (pub === "logiciel") return <Suspense fallback={<Chargement />}><LogicielView t={t} lang={lang} setLang={setLang} onLogin={goLogin} onBack={() => setPub("home")} /></Suspense>;
    return <LoginView t={t} lang={lang} setLang={setLang} onBack={() => { window.history.replaceState(null, "", "#"); setPub("home"); }} onLogin={(u) => { setUser(u); if (!parseHash()) setView("analyse"); }} />;
  }

  const isAdmin = user.role === "admin";
  const canWrite = isAdmin || user.role === "saisie";
  const TOP_NAV = [
    ["analyse", t.nav.analyse, BarChart3],
    ["qc", t.nav.qc, ClipboardCheck],
    ["map", t.nav.map, MapIcon],
    ["overlap", t.nav.overlap, GitCompare],
  ];
  const RAIL_NAV = [
    ["search", t.nav.search, Search],
    ["saisie", t.nav.saisie, PenLine],
    ["alerts", t.nav.alerts, BellRing],
    ["reports", t.nav.reports, FileText],
    ...(canWrite ? [["import", t.nav.import, Database]] : []),
    ["schema", t.nav.schema, Boxes],
    ["journal", t.nav.journal, History],
    ...(isAdmin ? [["users", t.nav.users, Users]] : []),
  ];
  const dataSheets = current?.sheets?.filter((s) => !s.pivot && s.n > 0) || [];
  const isSynthetic = (current?.filename || "").toLowerCase().startsWith("sample_test");
  const importId = current?.import_id || current?.id;

  return (
    <div className="App" style={{ minHeight: "100vh", background: "var(--bg)" }}>
      {user.doit_changer_mdp && <PasswordGate t={t} user={user} onDone={() => setUser({ ...user, doit_changer_mdp: false })} />}
      {!user.doit_changer_mdp && user.questions_definies === false && <QuestionsGate t={t} user={user} onDone={() => setUser({ ...user, questions_definies: true })} />}
      <header style={{ borderBottom: "1px solid var(--line)", background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 1000 }}>
        <div className="px-4 sm:px-6 lg:px-8" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 72 }}>
          <div className="flex items-center gap-3">
            <button className="btn lg:hidden" style={{ padding: "7px 9px" }} onClick={() => setRailMobile(true)} title={t.nav.menu} data-testid="rail-mobile-open"><Menu size={16} /></button>
            <Logo t={t} size={40} />
            <span className="badge ml-2 hidden md:inline-flex" style={{ background: "var(--card2)", color: "var(--tx2)" }}>{t.phase}</span>
            <nav className="hidden md:flex items-center gap-1.5 ml-4" data-testid="top-nav">
              {TOP_NAV.map(([k, lab, Icon]) => (
                <button key={k} className={`tab ${view === k ? "on" : ""}`} onClick={() => setView(k)} data-testid={`nav-${k}`} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                  <Icon size={15} />{lab}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <form onSubmit={(e) => { e.preventDefault(); goSearch(hdrQ); }} className="hidden sm:flex items-center" style={{ border: "1px solid var(--line2)", borderRadius: 4, background: "#fff", height: 36 }} data-testid="header-search-form">
              <input value={hdrQ} onChange={(e) => setHdrQ(e.target.value)} placeholder={t.search.placeholder} className="font-m text-sm" style={{ border: "none", outline: "none", background: "transparent", padding: "0 10px", width: 120, color: "var(--tx)" }} data-testid="header-search-input" />
              <button type="submit" className="btn" style={{ padding: "0 10px", height: 34, border: "none", borderLeft: "1px solid var(--line2)", borderRadius: 0 }} title={t.search.go} data-testid="header-search-submit"><Search size={15} /></button>
            </form>
            <span className="hidden sm:flex items-center gap-2 text-xs mr-1" data-testid="current-user">
              <span className="badge" style={{ background: user.role === "admin" ? "rgba(153,27,27,0.10)" : user.role === "saisie" ? "rgba(13,148,136,0.12)" : "var(--card2)", color: "var(--tx)" }}>{t.roles[user.role]}</span>
              <button className="font-m" style={{ color: "var(--tx2)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }} onClick={() => setAccount(true)} title={t.account.eyebrow} data-testid="account-button">{user.email}</button>
            </span>
            {installPrompt && <button className="btn hidden sm:inline-flex" style={{ padding: "7px 12px" }} onClick={async () => { installPrompt.prompt(); setInstallPrompt(null); }} data-testid="install-app-button"><Download size={15} />{t.hl.install}</button>}
            <button className="btn" style={{ padding: "7px 12px" }} onClick={() => setLang(lang === "fr" ? "en" : "fr")} data-testid="language-toggle-button"><Globe size={15} />{t.lang}</button>
            <button className="btn" style={{ padding: "7px 10px" }} onClick={logout} title={t.auth.logout} data-testid="logout-button"><LogOut size={15} /></button>
          </div>
        </div>
      </header>

      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <aside className={`rail ${railOpen ? "" : "rail-mini"} ${railMobile ? "rail-mobile-open" : ""}`} data-testid="side-rail">
          <div className="rail-head">
            {railOpen && <span className="eyebrow" style={{ fontSize: 10 }}>{t.nav.tools}</span>}
            <button className="rail-toggle hidden lg:inline-flex" onClick={toggleRail} title={railOpen ? t.nav.collapse : t.nav.expand} data-testid="rail-toggle">
              {railOpen ? <PanelLeftClose size={15} /> : <PanelLeftOpen size={15} />}
            </button>
            <button className="rail-toggle lg:hidden" onClick={() => setRailMobile(false)} data-testid="rail-mobile-close"><X size={15} /></button>
          </div>
          <nav className="rail-nav">
            {TOP_NAV.map(([k, lab, Icon]) => (
              <button key={k} className={`rail-item md:hidden ${view === k ? "on" : ""}`} onClick={() => { setView(k); setRailMobile(false); }} data-testid={`rail-nav-${k}`} title={lab}>
                <Icon size={16} /><span className="rail-label">{lab}</span>
              </button>
            ))}
            {RAIL_NAV.map(([k, lab, Icon]) => (
              <button key={k} className={`rail-item ${view === k ? "on" : ""}`} onClick={() => { setView(k); setRailMobile(false); window.scrollTo(0, 0); }} data-testid={`nav-${k}`} title={lab}>
                <Icon size={16} /><span className="rail-label">{lab}</span>
              </button>
            ))}
          </nav>
          {railOpen && current && (
            <div className="rail-foot">
              <div className="text-[11px] font-m" style={{ color: "var(--tx3)" }}>{intf(schema?.counts?.echantillons || 0)} {t.samples}</div>
              <div className="text-[11px] font-m" style={{ color: "var(--tx3)" }}>{schema?.counts?.imports || 0} {t.importsCount}</div>
              {isAdmin && <button className="btn mt-2 w-full justify-center" style={{ padding: "6px 10px", fontSize: 11 }} onClick={doReset} data-testid="reset-btn"><Trash2 size={13} />{t.reset}</button>}
            </div>
          )}
        </aside>
        {railMobile && <div className="rail-scrim lg:hidden" onClick={() => setRailMobile(false)} />}

        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-8">
          {(horsLigne || dateCache) && (
            <div className="card mb-6 flex flex-wrap items-center justify-between gap-3" style={{ padding: "12px 16px", borderColor: "var(--amber)", background: "rgba(217,119,6,0.06)" }} data-testid="offline-banner">
              <span className="text-sm flex items-center gap-2" style={{ color: "var(--amber)" }}>
                <WifiOff size={15} />
                {horsLigne ? t.hl.banner : t.hl.cached}
                {dateCache && <span className="font-m">· {t.hl.asOf} {new Date(dateCache).toLocaleString("fr-FR")}</span>}
              </span>
              <button className="btn" style={{ padding: "5px 12px", fontSize: 12 }} onClick={reconnecter} data-testid="offline-refresh"><RefreshCw size={13} />{t.hl.refresh}</button>
            </div>
          )}
          {horsLigne && !OFFLINE_VIEWS.includes(view) && (
            <div className="card" style={{ padding: 28, textAlign: "center" }} data-testid="offline-unavailable">
              <WifiOff size={22} color="var(--tx3)" />
              <h2 className="font-h text-xl mt-3">{t.hl.unavailable}</h2>
              <p className="text-sm mt-2" style={{ color: "var(--tx2)" }}>{t.hl.unavailableSub}</p>
              <button className="btn mt-4" onClick={() => setView("analyse")} data-testid="offline-go-dashboard"><BarChart3 size={14} />{t.nav.analyse}</button>
            </div>
          )}
          {alert && alert.n > 0 && view !== "alerts" && (
            <div className="card fade-up mb-6 flex flex-wrap items-center justify-between gap-3" style={{ padding: "14px 18px", borderColor: "var(--amber)" }} data-testid="alert-banner">
              <span className="text-sm flex items-center gap-2" style={{ color: "var(--amber)" }}><BellRing size={15} /><b className="font-m">{alert.n}</b> {t.alerts.banner}</span>
              <button className="btn" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => setView("alerts")} data-testid="alert-banner-link">{t.alerts.see}</button>
            </div>
          )}

          {SHEET_VIEWS.includes(view) && dataSheets.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mb-6">
              <span className="eyebrow mr-1">{t.chooseSheet}</span>
              {dataSheets.map((s) => (
                <button key={s.name} className={`tab ${sheet === s.name ? "on" : ""}`} onClick={() => setSheet(s.name)} data-testid={`sheet-tab-${s.name}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {s.hidden && <span className="h-1.5 w-1.5 rounded-sm" style={{ background: "#94A3B8" }} />}
                  {s.virtual && <PenLine size={11} color="var(--accent)" />}
                  <span className="font-m text-xs">{s.name}</span>
                  <span style={{ opacity: .7 }}>{intf(s.n)}</span>
                </button>
              ))}
              {dataSheets.some((s) => s.virtual) && sheet !== "Saisie (app)" && (
                <button className={`tab ${withSaisies ? "on" : ""}`} onClick={toggleSaisies} data-testid="toggle-saisies-btn" title={t.withSaisiesHelp}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 8, border: "1px dashed var(--line2)" }}>
                  <PenLine size={11} />{withSaisies ? t.withSaisiesOn : t.withSaisiesOff}
                </button>
              )}
            </div>
          )}

          <Suspense fallback={<Chargement />}>
            {(!horsLigne || OFFLINE_VIEWS.includes(view)) && <>
            {view === "import" && canWrite && <ImportView t={t} onCommitted={onCommitted} imports={imports} canDelete={canWrite} onDeleted={reloadImports} />}
            {view === "analyse" && <Dashboard key={`d${withSaisies}`} t={t} a={analyse} importId={importId} feuille={sheet} tab={tab} setTab={setTab} synthetic={isSynthetic} />}
            {view === "qc" && <QcView key={`q${withSaisies}${lang}`} t={t} importId={importId} feuille={sheet} a={analyse} onOpenSample={goSearch} canEdit={canWrite} />}
            {view === "map" && <MapView key={`m${withSaisies}`} t={t} importId={importId} feuille={sheet} canEdit={isAdmin} />}
            {view === "overlap" && <OverlapView t={t} data={overlap} />}
            {view === "search" && <SearchView t={t} query={query} setQuery={setQuery} canEdit={canWrite} />}
            {view === "saisie" && <SaisieView t={t} user={user} />}
            {view === "alerts" && <AlertsView t={t} user={user} onOpenSample={goSearch} />}
            {view === "reports" && <ReportsView t={t} user={user} lang={lang} />}
            {view === "schema" && <SchemaView t={t} data={schema} />}
            {view === "journal" && <JournalView t={t} items={journal} canEdit={canWrite} onOpenSample={goSearch} />}
            {view === "users" && isAdmin && <UsersView t={t} me={user} />}
            </>}
          </Suspense>
        </main>
      </div>
      <Suspense fallback={null}>
        {account && <AccountPanel t={t} user={user} onClose={() => setAccount(false)} />}
        {!horsLigne && <Copilote t={t} lang={lang} importId={importId} feuille={sheet} />}
      </Suspense>
    </div>
  );
}
